// components/CountryModal.js - Modal popup for viewing phone ranges
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Phone, Copy, CheckCircle } from 'lucide-react';

const CountryModal = ({ showModal, selectedCountry, onClose }) => {
    const [copySuccess, setCopySuccess] = useState(false);

    // Copy individual country ranges to clipboard
    const copyAllRanges = async () => {
        if (!selectedCountry || !selectedCountry.ranges || selectedCountry.ranges.length === 0) {
            return;
        }

        try {
            // Create formatted text with all ranges
            const rangesText = selectedCountry.ranges.join('\n');

            // Copy to clipboard
            await navigator.clipboard.writeText(rangesText);

            // Show success feedback
            setCopySuccess(true);
            console.log(`✅ Copied ${selectedCountry.ranges.length} ranges to clipboard`);

            // Reset success status after 2 seconds
            setTimeout(() => {
                setCopySuccess(false);
            }, 2000);

        } catch (error) {
            console.error('❌ Failed to copy ranges:', error);

            // Fallback for older browsers
            try {
                const textArea = document.createElement('textarea');
                textArea.value = selectedCountry.ranges.join('\n');
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);

                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
                console.log('✅ Copied ranges using fallback method');
            } catch (fallbackError) {
                console.error('❌ Fallback copy also failed:', fallbackError);
                alert('Failed to copy ranges to clipboard');
            }
        }
    };

    const handleClose = () => {
        setCopySuccess(false);
        onClose();
    };

    if (!showModal || !selectedCountry) return null;

    return ReactDOM.createPortal(
        <div
            style={{
                position: 'fixed',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                zIndex: '999999',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                backdropFilter: 'blur(4px)'
            }}
            onClick={handleClose}
        >
            <div
                style={{
                    backgroundColor: '#0f172a',
                    borderRadius: '16px',
                    width: '100%',
                    maxWidth: '900px',
                    height: '85vh',
                    border: '1px solid rgba(148, 163, 184, 0.2)',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    padding: '24px',
                    borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            backgroundColor: '#dc2626',
                            borderRadius: '8px',
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Phone size={20} color="white" />
                        </div>
                        <div>
                            <h3 style={{
                                color: 'white',
                                margin: '0',
                                fontSize: '1.25rem',
                                fontWeight: '600'
                            }}>
                                {selectedCountry.country}
                            </h3>
                            <p style={{
                                color: '#94a3b8',
                                margin: '2px 0 0 0',
                                fontSize: '0.875rem'
                            }}>
                                Phone Range Directory
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={handleClose}
                        style={{
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            color: '#ef4444',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            borderRadius: '8px',
                            padding: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                        }}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Stats Bar */}
                <div style={{
                    backgroundColor: 'rgba(30, 41, 59, 0.5)',
                    padding: '16px 24px',
                    borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
                    display: 'flex',
                    gap: '32px',
                    flexShrink: 0
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                            backgroundColor: '#10b981',
                            borderRadius: '50%',
                            width: '8px',
                            height: '8px'
                        }}></div>
                        <span style={{ color: '#e2e8f0', fontSize: '0.875rem' }}>
                            <strong>{selectedCountry.count.toLocaleString()}</strong> Total Messages
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                            backgroundColor: '#3b82f6',
                            borderRadius: '50%',
                            width: '8px',
                            height: '8px'
                        }}></div>
                        <span style={{ color: '#e2e8f0', fontSize: '0.875rem' }}>
                            <strong>{selectedCountry.ranges ? selectedCountry.ranges.length : 0}</strong> Unique Ranges
                        </span>
                    </div>
                </div>

                {/* Content - 3 Column List Format */}
                <div style={{
                    flex: '1',
                    overflow: 'auto',
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: '#1e293b',
                        borderRadius: '8px',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '1px'
                    }}>
                        {selectedCountry.ranges.map((range, index) => (
                            <div
                                key={index}
                                style={{
                                    padding: '12px 16px',
                                    backgroundColor: '#0f172a',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    transition: 'background-color 0.2s ease',
                                    borderBottom: '1px solid rgba(148, 163, 184, 0.1)'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(148, 163, 184, 0.05)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = '#0f172a';
                                }}
                            >
                                <div style={{
                                    color: '#9ca3af',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    minWidth: '30px',
                                    textAlign: 'right',
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none',
                                    MozUserSelect: 'none',
                                    pointerEvents: 'none'
                                }}>
                                    {index + 1}.
                                </div>
                                <div style={{
                                    color: '#e2e8f0',
                                    fontSize: '0.9rem',
                                    fontFamily: 'monospace',
                                    letterSpacing: '0.5px',
                                    fontWeight: '500',
                                    userSelect: 'text',
                                    WebkitUserSelect: 'text',
                                    MozUserSelect: 'text',
                                    cursor: 'text',
                                    padding: '4px 6px',
                                    borderRadius: '4px',
                                    transition: 'background-color 0.2s ease',
                                    flex: 1
                                }}
                                     onMouseOver={(e) => {
                                         e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                                     }}
                                     onMouseOut={(e) => {
                                         e.currentTarget.style.backgroundColor = 'transparent';
                                     }}
                                >
                                    {range}
                                </div>
                            </div>
                        ))}
                    </div>

                    {selectedCountry.ranges.length === 0 && (
                        <div style={{
                            textAlign: 'center',
                            padding: '60px 20px',
                            color: '#94a3b8'
                        }}>
                            <Phone size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                            <p style={{ fontSize: '1rem' }}>No phone ranges found for this country.</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    backgroundColor: 'rgba(30, 41, 59, 0.3)',
                    padding: '20px 24px',
                    borderTop: '1px solid rgba(148, 163, 184, 0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0,
                    gap: '16px'
                }}>
                    <span style={{
                        color: '#94a3b8',
                        fontSize: '0.875rem'
                    }}>
                        Displaying {selectedCountry.ranges ? selectedCountry.ranges.length : 0} phone ranges
                    </span>

                    <div style={{ display: 'flex', gap: '12px' }}>
                        {/* Copy All Ranges Button */}
                        <button
                            onClick={copyAllRanges}
                            disabled={!selectedCountry.ranges || selectedCountry.ranges.length === 0}
                            style={{
                                backgroundColor: copySuccess ? '#10b981' : '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 16px',
                                cursor: selectedCountry.ranges && selectedCountry.ranges.length > 0 ? 'pointer' : 'not-allowed',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                opacity: selectedCountry.ranges && selectedCountry.ranges.length > 0 ? 1 : 0.6
                            }}
                            onMouseOver={(e) => {
                                if (selectedCountry.ranges && selectedCountry.ranges.length > 0 && !copySuccess) {
                                    e.target.style.backgroundColor = '#2563eb';
                                    e.target.style.transform = 'translateY(-1px)';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!copySuccess) {
                                    e.target.style.backgroundColor = '#3b82f6';
                                    e.target.style.transform = 'translateY(0)';
                                }
                            }}
                        >
                            {copySuccess ? (
                                <>
                                    <CheckCircle size={16} />
                                    Copied!
                                </>
                            ) : (
                                <>
                                    <Copy size={16} />
                                    Copy All ({selectedCountry.ranges ? selectedCountry.ranges.length : 0})
                                </>
                            )}
                        </button>

                        <button
                            onClick={handleClose}
                            style={{
                                backgroundColor: '#374151',
                                color: 'white',
                                border: '1px solid rgba(148, 163, 184, 0.2)',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.backgroundColor = '#4b5563';
                                e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.backgroundColor = '#374151';
                                e.target.style.transform = 'translateY(0)';
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CountryModal;